import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Send, RefreshCw, Loader2, Trash2, Eye, Database, Clock, CheckCircle2, FileText, AlertTriangle } from 'lucide-react';
import { campaignsApi, CampaignRecord } from '../services/creatorApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
export function Campaigns() {
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const [campaigns, setCampaigns] = useState<CampaignRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorDebug, setErrorDebug] = useState<any>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const fetchCampaigns = async () => {
    setLoading(true);
    setError(null);
    setErrorDebug(null);
    try {
      console.log('[Campaigns] Fetching from Creator via campaignsApi.list()...');
      const data = await campaignsApi.list();
      console.log('[Campaigns] Received', data.length, 'campaigns:', data);
      setCampaigns(data);
    } catch (err: any) {
      const message = err instanceof Error ? err.message : String(err);
      const debug = err?.debug || null;
      console.error('[Campaigns] Failed to fetch:', message, debug);
      setError(message);
      setErrorDebug(debug);
      toast.error(`Failed to load campaigns: ${message}`);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchCampaigns();
  }, []);
  const handleDelete = async (id: string) => {
    setIsDeleting(true);
    try {
      await campaignsApi.delete(id);
      toast.success('Campaign deleted');
      setDeleteConfirm(null);
      fetchCampaigns();
    } catch (err) {
      console.error('Failed to delete campaign:', err);
      toast.error('Failed to delete campaign');
    } finally {
      setIsDeleting(false);
    }
  };
  // Filter by team and region
  const filteredCampaigns = campaigns.filter(c => {
    if (c.Team !== currentWorkspace.name) return false;
    if (currentSubRegion) {
      return !c.Region || c.Region === currentSubRegion;
    }
    return true;
  });
  // Debug: count how many were filtered out
  const filteredOutCount = campaigns.length - filteredCampaigns.length;
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Sent':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-100" data-id="element-3245" data-mp="e0vwlyokblmi">
            <CheckCircle2 className="h-3 w-3" data-id="element-3246" data-mp="4ywd8zq2s5ge" />
            Sent
          </span>;
      case 'Exported':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100" data-id="element-3247" data-mp="8zpytmg1p864">
            <Database className="h-3 w-3" data-id="element-3248" data-mp="23dr16hat0day" />
            Exported
          </span>;
      default:
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200" data-id="element-3249" data-mp="3nwb6fme83qd2">
            <FileText className="h-3 w-3" data-id="element-3250" data-mp="1fxbsoox978u" />
            Draft
          </span>;
    }
  };
  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };
  return <div className="space-y-6" data-id="element-3251" data-mp="kdq9nckouqzd">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-3252" data-mp="kcp05cjbmcjr">
        <div className="min-w-0" data-id="element-3253" data-mp="fghqp9rpuix2">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-3254" data-mp="2m5weojr40q4l">
            Email Campaigns
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-3255" data-mp="2h7bfmnnlgchq">
            Create audience buckets and export to Zoho Campaigns.
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-3256" data-mp="2jl6pomhjtv5s">
          <button onClick={fetchCampaigns} disabled={loading} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md" title="Refresh" data-id="element-3257" data-mp="2nn1uw22losou">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-3258" data-mp="2xlrcruuhyd53" />
          </button>
          <Link to="/campaigns/new" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap flex-shrink-0" data-id="element-3259" data-mp="1zyiltkbq8bvp">
            <Plus className="h-4 w-4 flex-shrink-0" data-id="element-3260" data-mp="29uc7pubc5hq5" />
            <span data-id="element-3261" data-mp="2en5aicn8ufzl">Create Campaign</span>
          </Link>
        </div>
      </div>

      {/* Error Display */}
      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-id="element-3262" data-mp="1h6ga8ndxzlsb">
          <div className="flex items-start gap-3" data-id="element-3263" data-mp="1m5styz9iocqo">
            <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-3264" data-mp="1m49fne7f8brj" />
            <div className="flex-1 min-w-0" data-id="element-3265" data-mp="1embatn06poun">
              <h3 className="text-sm font-medium text-red-800" data-id="element-3266" data-mp="19nzz16qbx49s">
                Failed to fetch campaigns from Creator
              </h3>
              <p className="mt-1 text-sm text-red-700 break-all" data-id="element-3267" data-mp="14ncacrpkoziq">{error}</p>
              {errorDebug && <details className="mt-2" data-id="element-3268" data-mp="zp10igza2p0i">
                  <summary className="text-xs text-red-600 cursor-pointer hover:text-red-800" data-id="element-3269" data-mp="vnz03wtmk6wy">
                    Show debug info
                  </summary>
                  <pre className="mt-1 text-xs text-red-600 bg-red-100 rounded p-2 overflow-auto max-h-40 font-mono" data-id="element-3270" data-mp="qvo1cxamer5z">
                    {JSON.stringify(errorDebug, null, 2)}
                  </pre>
                </details>}
              <button onClick={fetchCampaigns} disabled={loading} className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-red-100 text-red-700 text-sm font-medium rounded-md hover:bg-red-200 disabled:opacity-50" data-id="element-3271" data-mp="bzyj9a8k2y6j">
                {loading ? <Loader2 className="h-3 w-3 animate-spin" data-id="element-3272" data-mp="2cvoetg2gsxc" /> : <RefreshCw className="h-3 w-3" data-id="element-3273" data-mp="3khz04x0jg36q" />}
                Retry
              </button>
            </div>
          </div>
        </div>}

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-3274" data-mp="3phbwq9h7oomi">
        {loading && campaigns.length === 0 ? <div className="p-12 text-center" data-id="element-3275" data-mp="l2u5u05r7ydn">
            <Loader2 className="mx-auto h-8 w-8 text-blue-500 animate-spin" data-id="element-3276" data-mp="l1k7d9uzoo6h" />
            <p className="mt-2 text-sm text-gray-500" data-id="element-3277" data-mp="7c8nqdo4jsgt">Loading campaigns...</p>
          </div> : filteredCampaigns.length === 0 ? <div className="p-12 text-center" data-id="element-3278" data-mp="c8pbh6rew4td">
            <Send className="mx-auto h-12 w-12 text-gray-400" data-id="element-3279" data-mp="zzkxtod3m7t8" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-3280" data-mp="zy1iq136i2t1">
              No campaigns found
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-3281" data-mp="1e5mwejpsmxf5">
              Start engaging your audience by creating a campaign.
            </p>
            <Link to="/campaigns/new" className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-3282" data-mp="1a302md9kb2fb">
              <Plus className="h-4 w-4" data-id="element-3283" data-mp="1ixo3zsc0nzyu" />
              Create Campaign
            </Link>
          </div> : <div className="overflow-x-auto" data-id="element-3284" data-mp="v728t1n42a69">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-3285" data-mp="r5p1so8a80xr">
              <thead className="bg-gray-50" data-id="element-3286" data-mp="r7jni4rdnx2w">
                <tr data-id="element-3287" data-mp="155zu7vp8pnl1">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3288" data-mp="156jdpgujgk16">
                    Campaign
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3289" data-mp="2gj27ohw1lx0z">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3290" data-mp="9wnuprwdqt00">
                    Bucket ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3291" data-mp="iwt2421kpre6">
                    Recipients
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3292" data-mp="smcld8t77n9k">
                    Exported
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3293" data-mp="xnjrl3fp0ee4">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-3294" data-mp="33enlbqnmmsc2">
                {filteredCampaigns.map(campaign => <tr key={campaign.ID} className="hover:bg-gray-50" data-id="element-3295" data-mp="38ejppr08kls5">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3296" data-mp="30tpls85f1458">
                      <div className="text-sm font-medium text-gray-900 truncate max-w-[250px]" data-id="element-3297" data-mp="30w2bj7hf0isu">
                        {campaign.Campaign_Name}
                      </div>
                      <div className="text-xs text-gray-500 truncate max-w-[250px]" data-id="element-3298" data-mp="2r4xy4c7e1604">
                        {campaign.Email_Subject}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3299" data-mp="3jrseawx69j7s">
                      {getStatusBadge(campaign.Status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3300" data-mp="3erxokwp4uzob">
                      {campaign.Bucket_ID ? <code className="text-xs font-mono bg-purple-50 text-purple-700 px-2 py-1 rounded border border-purple-100" data-id="element-3301" data-mp="35t2h7hacrjjf">
                          {campaign.Bucket_ID}
                        </code> : <span className="text-sm text-gray-400" data-id="element-3302" data-mp="1ucuar4rsvo2h">-</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600" data-id="element-3303" data-mp="32ngkhca89e2u">
                      {campaign.Recipient_Count ? campaign.Recipient_Count.toLocaleString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3304" data-mp="37li3jx7nldeb">
                      {formatDate(campaign.Exported_At)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-3305" data-mp="3mcr3p096mh5p">
                      <div className="flex items-center justify-end gap-2" data-id="element-3306" data-mp="3he5z6omwdbbg">
                        <Link to={`/campaigns/${campaign.ID}`} className="text-gray-400 hover:text-blue-600 p-2 hover:bg-blue-50 rounded" title="View Details" data-id="element-3307" data-mp="3vata5vboq20d">
                          <Eye className="h-4 w-4" data-id="element-3308" data-mp="1vza2rl6151st" />
                        </Link>
                        <button onClick={() => setDeleteConfirm(campaign.ID)} className="text-gray-400 hover:text-red-600 p-2 hover:bg-red-50 rounded" title="Delete" data-id="element-3309" data-mp="bfxtqhyv0kpc">
                          <Trash2 className="h-4 w-4" data-id="element-3310" data-mp="2mbcrxzb9m07" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>

      {/* Delete Confirmation */}
      {deleteConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-3311" data-mp="3fuxmq4jnuje1">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-3312" data-mp="3fwh45yh1z0he">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setDeleteConfirm(null)} data-id="element-3313" data-mp="3jx0zqxlz9w1f" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-sm p-6" data-id="element-3314" data-mp="1i4waxuzrzeux">
              <div className="flex items-center gap-4 mb-4" data-id="element-3315" data-mp="1i2jw3qpbdkm3">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center" data-id="element-3316" data-mp="2r2mho4rdtg1w">
                  <Trash2 className="h-5 w-5 text-red-600" data-id="element-3317" data-mp="2m1f7jz1ro4s2" />
                </div>
                <div data-id="element-3318" data-mp="2i1y2v7u5zbny">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-3319" data-mp="2i393wzw865bd">
                    Delete Campaign
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-3320" data-mp="39s2t3n4lusx3">
                    Are you sure? This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3" data-id="element-3321" data-mp="35t3gdgcmtrv3">
                <button onClick={() => setDeleteConfirm(null)} disabled={isDeleting} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-3322" data-mp="30yprzxwe0kzx">
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteConfirm)} disabled={isDeleting} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2" data-id="element-3323" data-mp="3ooacwmc3psbu">
                  {isDeleting && <Loader2 className="h-4 w-4 animate-spin" data-id="element-3324" data-mp="9vw5u77hd0iv" />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}